import {
  Mail, School, Calendar, Users, MessageCircle,
  Lock, Edit3, Award, LogOut, ChevronRight,
  ShieldCheck, Trophy, Star, Loader2, Bell, Monitor,
  ExternalLink, Moon, Sun, X, Send, BookOpen,
} from "lucide-react"
import { useApp }  from "@/App"
import { useAuth } from "@/contexts/AuthContext"
import { useState, useEffect } from "react"
import { API_URL } from '@/services/apiClient'
import { studentService } from "@/services/studentService"
import { firebaseMessagingService } from "@/config/firebase"
import { useStudentNavigation } from "@/contexts/StudentNavigationContext"
import { safeLog } from '@/utils/safeLog';

interface ProfileStats {
  points: number
  level: number
  levelName: string
  rank: number | null
  totalClassmates: number | null
  achievementsUnlocked: number
}

export function StudentProfile() {
  const { user, logout: appLogout }         = useApp()
  const { currentUser, logout: authLogout } = useAuth()
  const { setCurrentPage }                  = useStudentNavigation()

  const storedUser = (() => {
    try { return JSON.parse(sessionStorage.getItem('user') || '{}') } catch { return {} }
  })()

  const [schoolName, setSchoolName]         = useState<string>(storedUser.schoolName || '')
  const [schoolLoading, setSchoolLoading]   = useState(!storedUser.schoolName)
  const [stats, setStats]                   = useState<ProfileStats | null>(null)
  const [statsLoading, setStatsLoading]     = useState(true)
  const [notifPerm, setNotifPerm]           = useState<NotificationPermission | 'unsupported'>('default')
  const [monitoringOn, setMonitoringOn]     = useState(() => localStorage.getItem('monitoringEnabled') !== 'false')
  const [darkMode, setDarkMode]             = useState(() => localStorage.getItem('studentDarkMode') === 'true')
  const [showSupport, setShowSupport]       = useState(false)
  const [supportMsg, setSupportMsg]         = useState('')
  const [supportSending, setSupportSending] = useState(false)
  const [supportSent, setSupportSent]       = useState(false)

  const dm = darkMode

  // Sync with shell dark-mode toggle
  useEffect(() => {
    const handler = (e: Event) => setDarkMode((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  useEffect(() => {
    if (storedUser.schoolName) return
    if (!user) { setSchoolLoading(false); return }
    const token = sessionStorage.getItem('authToken')
    fetch(`${API_URL}/schools/my-school`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSchoolName(d?.school?.name || ''))
      .catch(() => setSchoolName(''))
      .finally(() => setSchoolLoading(false))
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const [dashboard, gamification] = await Promise.all([
          studentService.getDashboard(),
          studentService.getGamification(),
        ])
        if (!cancelled) {
          setStats({
            points:               gamification.points,
            level:                gamification.level,
            levelName:            gamification.levelName,
            rank:                 dashboard.rank?.position ?? null,
            totalClassmates:      dashboard.rank?.totalClassmates ?? null,
            achievementsUnlocked: gamification.achievements?.filter((a: { unlocked: boolean }) => a.unlocked).length ?? 0,
          })
        }
      } catch {
        // Non-fatal
      } finally {
        if (!cancelled) setStatsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      setNotifPerm('unsupported')
    } else {
      setNotifPerm(Notification.permission)
    }
  }, [])

  const requestNotifPerm = async () => {
    if (typeof Notification === 'undefined') return
    const result = await Notification.requestPermission()
    setNotifPerm(result)
    if (result === 'granted') {
      const uid = sessionStorage.getItem('authUid') ?? currentUser?.id ?? user?.id ?? null
      if (uid) firebaseMessagingService.getTokenAndRegister(uid).catch(() => {/* non-fatal */})
    }
  }

  const toggleMonitoring = (on: boolean) => {
    setMonitoringOn(on)
    localStorage.setItem('monitoringEnabled', String(on))
  }

  const toggleDarkMode = (on: boolean) => {
    setDarkMode(on)
    localStorage.setItem('studentDarkMode', String(on))
    window.dispatchEvent(new CustomEvent('studentDarkModeChanged', { detail: { darkMode: on } }))
  }

  const handleLogout = async () => {
    try { await authLogout(); appLogout() }
    catch (err) { safeLog.error('Logout error:', err) }
  }

  const handleSupportSubmit = async () => {
    if (!supportMsg.trim()) return
    setSupportSending(true)
    try {
      const token = sessionStorage.getItem('authToken')
      await fetch(`${API_URL}/support/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: supportMsg, source: 'student_profile' }),
      })
    } catch { /* non-fatal */ }
    setSupportSending(false)
    setSupportSent(true)
    setSupportMsg('')
  }

  const getDisplayName = (): string => {
    const name = storedUser.name || user?.name || currentUser?.user_metadata?.full_name
    if (name && name !== getEmail()?.split('@')[0]) return name
    const email = getEmail()
    if (email) return email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
    return 'Student'
  }

  const getEmail = (): string => storedUser.email || user?.email || currentUser?.email || ''

  const getEnrollmentDate = (): string => {
    const raw = storedUser.createdAt || currentUser?.created_at
    if (raw) {
      const d = new Date(raw)
      if (Date.now() - d.getTime() > 5 * 60 * 1000)
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    }
    return 'Unknown'
  }

  const generateStudentId = (email: string): string => {
    const prefix = email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase()
    return `SH-${prefix}`
  }

  const initials = getDisplayName()
    .split(' ')
    .map((p: string) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const isVerified = !!currentUser?.email_confirmed_at

  const stripItems = [
    { label: 'Points', value: statsLoading ? '…' : (stats?.points ?? 0).toLocaleString(), icon: Star,   iconColor: 'text-amber-400'  },
    { label: 'Rank',   value: statsLoading ? '…' : stats?.rank != null ? `#${stats.rank}` : '—',        icon: Trophy, iconColor: 'text-purple-400' },
    { label: 'Badges', value: statsLoading ? '…' : stats?.achievementsUnlocked ?? 0,                    icon: Award,  iconColor: 'text-blue-400'   },
  ]

  // Glass card — matches StudentHome glassCard pattern
  const glassCard = dm
    ? 'bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 shadow-md'
    : 'bg-white/75 backdrop-blur-sm rounded-2xl border border-white/60 shadow-md'

  return (
    <>
    {/* No background override — the shell's gradient shows through */}
    <div className="pb-6">

      {/* ── Profile hero ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-800 px-4 pt-8 pb-16 text-white">
        <div className="absolute -top-10 -right-10 w-36 h-36 bg-white/10 rounded-full pointer-events-none" />
        <div className="absolute -bottom-6 -left-4  w-28 h-28 bg-white/5  rounded-full pointer-events-none" />

        <div className="relative flex flex-col items-center text-center">
          <div className="relative mb-3">
            <div className="w-24 h-24 rounded-3xl bg-white/20 border-4 border-white/30 flex items-center justify-center overflow-hidden shadow-xl">
              {currentUser?.user_metadata?.avatar_url ? (
                <img src={currentUser.user_metadata.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-white">{initials}</span>
              )}
            </div>
            <button
              onClick={() => alert('Profile picture upload coming soon!')}
              className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
              style={{ border: 'none', padding: 0, cursor: 'pointer', lineHeight: 1, background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(4px)' }}
            >
              <Edit3 className="w-3.5 h-3.5 text-white" />
            </button>
          </div>

          <h1 className="text-xl font-bold mb-0.5">{getDisplayName()}</h1>
          {getEmail() && (
            <p className="text-indigo-200 text-sm">
              {generateStudentId(getEmail())}
              {stats && !statsLoading && <span className="ml-2">· {stats.levelName}</span>}
            </p>
          )}
          {isVerified && (
            <div className="mt-2 flex items-center gap-1.5 bg-white/15 px-3 py-1 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-xs font-medium text-emerald-200">Verified Account</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Stats strip (pulled up over hero) ── */}
      <div className="mx-4 -mt-8 relative z-10">
        <div className={`${glassCard} grid grid-cols-3 divide-x ${dm ? 'divide-white/10' : 'divide-gray-100'}`}>
          {stripItems.map(({ label, value, icon: Icon, iconColor }) => (
            <div key={label} className="flex flex-col items-center py-4 gap-1">
              <Icon className={`w-4 h-4 ${iconColor}`} />
              <span className={`text-lg font-bold leading-tight ${dm ? 'text-white' : 'text-gray-900'}`}>
                {statsLoading
                  ? <Loader2 className={`w-4 h-4 animate-spin ${dm ? 'text-white/30' : 'text-gray-300'}`} />
                  : value}
              </span>
              <span className={`text-[11px] ${dm ? 'text-white/50' : 'text-gray-500'}`}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 mt-4 space-y-4">

        {/* ── Personal info ── */}
        <Section title="Personal Information" dm={dm} glassCard={glassCard}>
          <InfoRow
            icon={Mail}     iconBg={dm ? 'bg-blue-500/20'   : 'bg-blue-50'}   iconColor={dm ? 'text-blue-300'   : 'text-blue-600'}
            value={getEmail() || 'No email available'} dm={dm}
          />
          <InfoRow
            icon={School}   iconBg={dm ? 'bg-emerald-500/20' : 'bg-green-50'}  iconColor={dm ? 'text-emerald-300' : 'text-green-600'}
            value={schoolLoading ? 'Loading…' : schoolName || 'School not set'} dm={dm}
          />
          <InfoRow
            icon={Calendar} iconBg={dm ? 'bg-purple-500/20' : 'bg-purple-50'} iconColor={dm ? 'text-purple-300' : 'text-purple-600'}
            value={`Enrolled: ${getEnrollmentDate()}`} dm={dm}
          />
          {currentUser?.last_sign_in_at && (
            <InfoRow
              icon={Users}  iconBg={dm ? 'bg-amber-500/20'  : 'bg-orange-50'} iconColor={dm ? 'text-amber-300'  : 'text-orange-600'}
              value={`Last active: ${new Date(currentUser.last_sign_in_at).toLocaleDateString()}`} dm={dm}
            />
          )}
        </Section>

        {/* ── Account status ── */}
        <Section title="Account Status" dm={dm} glassCard={glassCard}>
          <StatusRow
            label="Email Verification"
            badge={isVerified ? 'Verified' : 'Pending'}
            badgeClass={isVerified
              ? dm ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-800'
              : dm ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'       : 'bg-amber-100 text-amber-800'}
            dm={dm}
          />
          <StatusRow
            label="Account Type"
            badge="Student"
            badgeClass={dm ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'bg-indigo-100 text-indigo-800'}
            dm={dm}
          />
          <StatusRow
            label="Security Level"
            badge="High"
            badgeClass={dm ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-800'}
            dm={dm}
          />
        </Section>

        {/* ── App & Permissions ── */}
        <Section title="App & Permissions" dm={dm} glassCard={glassCard}>
          {/* Notification permission */}
          <div className="flex items-center gap-3 px-1 py-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? 'bg-indigo-500/20' : 'bg-indigo-50'}`}>
              <Bell className={`w-4 h-4 ${dm ? 'text-indigo-300' : 'text-indigo-600'}`} />
            </div>
            <span className={`flex-1 text-sm ${dm ? 'text-white/80' : 'text-gray-700'}`}>Push Notifications</span>
            {notifPerm === 'granted' ? (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${dm ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-emerald-100 text-emerald-800'}`}>Enabled</span>
            ) : notifPerm === 'denied' ? (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${dm ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30' : 'bg-red-100 text-red-700'}`}>Blocked</span>
            ) : notifPerm === 'unsupported' ? (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${dm ? 'bg-white/10 text-white/40 border border-white/10' : 'bg-gray-100 text-gray-500'}`}>Unsupported</span>
            ) : (
              <button
                onClick={requestNotifPerm}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${dm ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/30' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                style={{ border: dm ? undefined : 'none', cursor: 'pointer' }}
              >
                Enable
              </button>
            )}
          </div>

          {/* Dark mode toggle */}
          <div className="flex items-center gap-3 px-1 py-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? 'bg-white/10' : 'bg-slate-100'}`}>
              {dm ? <Moon className="w-4 h-4 text-indigo-300" /> : <Sun className="w-4 h-4 text-slate-500" />}
            </div>
            <span className={`flex-1 text-sm ${dm ? 'text-white/80' : 'text-gray-700'}`}>Dark Mode</span>
            <button
              role="switch"
              aria-checked={darkMode}
              onClick={() => toggleDarkMode(!darkMode)}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center',
                width: '2.5rem', height: '1.375rem', borderRadius: '9999px',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'background-color 0.2s ease',
                backgroundColor: darkMode ? '#6366f1' : '#cbd5e1', outline: 'none',
              }}
            >
              <span style={{
                display: 'block', width: '1.125rem', height: '1.125rem', borderRadius: '50%',
                backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                transition: 'transform 0.2s ease',
                transform: darkMode ? 'translateX(1.25rem)' : 'translateX(0.125rem)',
              }} />
            </button>
          </div>

          {/* Safety monitoring toggle */}
          <div className="flex items-center gap-3 px-1 py-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? 'bg-emerald-500/20' : 'bg-emerald-50'}`}>
              <Monitor className={`w-4 h-4 ${dm ? 'text-emerald-300' : 'text-emerald-600'}`} />
            </div>
            <span className={`flex-1 text-sm ${dm ? 'text-white/80' : 'text-gray-700'}`}>Safety Monitoring</span>
            <button
              role="switch"
              aria-checked={monitoringOn}
              onClick={() => toggleMonitoring(!monitoringOn)}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center',
                width: '2.5rem', height: '1.375rem', borderRadius: '9999px',
                border: 'none', cursor: 'pointer', padding: 0,
                transition: 'background-color 0.2s ease',
                backgroundColor: monitoringOn ? '#10b981' : dm ? 'rgba(255,255,255,0.15)' : '#cbd5e1', outline: 'none',
              }}
            >
              <span style={{
                display: 'block', width: '1.125rem', height: '1.125rem', borderRadius: '50%',
                backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                transition: 'transform 0.2s ease',
                transform: monitoringOn ? 'translateX(1.25rem)' : 'translateX(0.125rem)',
              }} />
            </button>
          </div>

          {/* Desktop app link */}
          <button
            onClick={() => window.open('https://github.com/anthropics/shieldedhaven/releases', '_blank')}
            className={`w-full flex items-center gap-3 px-1 py-2 rounded-xl transition-colors text-left ${dm ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
              <img src="/pwa-192x192.png" alt="Shield" className="w-8 h-8 object-contain" />
            </div>
            <span className={`flex-1 text-sm ${dm ? 'text-white/80' : 'text-gray-700'}`}>Download Desktop App</span>
            <ExternalLink className={`w-4 h-4 ${dm ? 'text-white/25' : 'text-gray-300'}`} />
          </button>
        </Section>

        {/* ── Resources ── */}
        <Section title="Resources" dm={dm} glassCard={glassCard}>
          <button
            onClick={() => setCurrentPage('documentation')}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${dm ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
            style={{ background: 'none', border: 'none' }}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-indigo-500/20' : 'bg-indigo-50'}`}>
              <BookOpen className={`w-4 h-4 ${dm ? 'text-indigo-300' : 'text-indigo-600'}`} />
            </div>
            <span className={`flex-1 text-sm font-medium ${dm ? 'text-white/80' : 'text-gray-800'}`}>User Guide</span>
            <ChevronRight className={`w-4 h-4 ${dm ? 'text-white/25' : 'text-gray-300'}`} />
          </button>

          <button
            onClick={() => { setShowSupport(true); setSupportSent(false) }}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${dm ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
            style={{ background: 'none', border: 'none' }}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
              <Users className={`w-4 h-4 ${dm ? 'text-blue-300' : 'text-blue-600'}`} />
            </div>
            <span className={`flex-1 text-sm font-medium ${dm ? 'text-white/80' : 'text-gray-800'}`}>Help & Support</span>
            <ChevronRight className={`w-4 h-4 ${dm ? 'text-white/25' : 'text-gray-300'}`} />
          </button>

          <button
            onClick={() => window.open('https://shieldedhaven.com/contact', '_blank')}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${dm ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
            style={{ background: 'none', border: 'none' }}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-emerald-500/20' : 'bg-green-50'}`}>
              <MessageCircle className={`w-4 h-4 ${dm ? 'text-emerald-300' : 'text-green-600'}`} />
            </div>
            <span className={`flex-1 text-sm font-medium ${dm ? 'text-white/80' : 'text-gray-800'}`}>Contact Us</span>
            <ExternalLink className={`w-4 h-4 ${dm ? 'text-white/25' : 'text-gray-300'}`} />
          </button>

          <button
            onClick={() => window.open('https://shieldedhaven.com/privacy', '_blank')}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${dm ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
            style={{ background: 'none', border: 'none' }}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-purple-500/20' : 'bg-purple-50'}`}>
              <Lock className={`w-4 h-4 ${dm ? 'text-purple-300' : 'text-purple-600'}`} />
            </div>
            <span className={`flex-1 text-sm font-medium ${dm ? 'text-white/80' : 'text-gray-800'}`}>Privacy Policy</span>
            <ExternalLink className={`w-4 h-4 ${dm ? 'text-white/25' : 'text-gray-300'}`} />
          </button>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${dm ? 'hover:bg-rose-500/10' : 'hover:bg-rose-50'}`}
            style={{ background: 'none', border: 'none' }}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-rose-500/20' : 'bg-rose-50'}`}>
              <LogOut className="w-4 h-4 text-rose-400" />
            </div>
            <span className="flex-1 text-sm font-medium text-rose-400">Log Out</span>
          </button>
        </Section>

      </div>
    </div>

    {/* ── Help & Support modal ── */}
    {showSupport && (
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}
        onClick={e => { if (e.target === e.currentTarget) setShowSupport(false) }}
      >
        <div className={`w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden ${dm ? 'bg-slate-900/90 backdrop-blur-xl border border-white/10' : 'bg-white'}`}>
          <div className="bg-gradient-to-br from-blue-600 to-indigo-600 px-5 pt-5 pb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-base leading-tight">Help & Support</h2>
                <p className="text-white/60 text-xs">We'll get back to you shortly</p>
              </div>
            </div>
            <button
              onClick={() => setShowSupport(false)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer' }}
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {supportSent ? (
              <div className="text-center py-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${dm ? 'bg-emerald-500/20' : 'bg-green-100'}`}>
                  <ShieldCheck className={`w-7 h-7 ${dm ? 'text-emerald-300' : 'text-green-600'}`} />
                </div>
                <p className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>Message sent!</p>
                <p className={`text-xs mt-1 ${dm ? 'text-white/50' : 'text-gray-500'}`}>Our team will respond within 24 hours.</p>
                <button
                  onClick={() => setShowSupport(false)}
                  className="mt-4 px-6 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
                  style={{ border: 'none', cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div>
                  <label className={`text-xs font-semibold block mb-1.5 ${dm ? 'text-white/50' : 'text-gray-500'}`}>
                    How can we help?
                  </label>
                  <textarea
                    value={supportMsg}
                    onChange={e => setSupportMsg(e.target.value)}
                    placeholder="Describe your issue or question…"
                    rows={4}
                    className={`w-full rounded-xl px-3 py-2.5 text-sm resize-none outline-none transition-colors ${
                      dm
                        ? 'bg-white/10 border border-white/10 text-white placeholder:text-white/30 focus:border-indigo-400'
                        : 'bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-indigo-400'
                    }`}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSupport(false)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${dm ? 'text-white/50 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-50'}`}
                    style={{ border: 'none', background: 'none', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSupportSubmit}
                    disabled={!supportMsg.trim() || supportSending}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    style={{ border: 'none', cursor: supportMsg.trim() ? 'pointer' : 'not-allowed' }}
                  >
                    {supportSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {supportSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, children, dm, glassCard }: {
  title: string; children: React.ReactNode; dm: boolean; glassCard: string
}) {
  return (
    <div className={glassCard}>
      <div className={`px-4 py-3 border-b ${dm ? 'border-white/10' : 'border-gray-100'}`}>
        <h2 className={`text-xs font-semibold uppercase tracking-wider ${dm ? 'text-white/50' : 'text-gray-500'}`}>{title}</h2>
      </div>
      <div className="p-3 space-y-1">{children}</div>
    </div>
  )
}

function InfoRow({
  icon: Icon, iconBg, iconColor, value, dm,
}: {
  icon: typeof Mail; iconBg: string; iconColor: string; value: string; dm: boolean
}) {
  return (
    <div className="flex items-center gap-3 px-1 py-2">
      <div className={`${iconBg} w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>
      <span className={`text-sm truncate ${dm ? 'text-white/80' : 'text-gray-700'}`}>{value}</span>
    </div>
  )
}

function StatusRow({
  label, badge, badgeClass, dm,
}: {
  label: string; badge: string; badgeClass: string; dm: boolean
}) {
  return (
    <div className="flex items-center justify-between px-1 py-2">
      <span className={`text-sm ${dm ? 'text-white/80' : 'text-gray-700'}`}>{label}</span>
      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badgeClass}`}>{badge}</span>
    </div>
  )
}
